import React from 'react';
import { createRoot } from 'react-dom/client';
import type { BoardFloorPlanData } from '../../../types';
import FloorPlanPrintSheet from './FloorPlanPrintSheet';
import {
  floorPlanPdfDate,
  floorPlanPdfLayout,
  type FloorPlanPdfPaper,
} from './floorPlanPdf';

function waitFrames(count = 2): Promise<void> {
  return new Promise((resolve) => {
    const step = (left: number) => {
      if (left <= 0) {
        resolve();
        return;
      }
      requestAnimationFrame(() => step(left - 1));
    };
    step(count);
  });
}

async function svgToPngDataUrl(svg: SVGSVGElement, width: number, height: number): Promise<string> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  const xml = `<?xml version="1.0" encoding="UTF-8"?>${new XMLSerializer().serializeToString(clone)}`;
  const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not rasterize floor plan'));
      img.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadFloorPlanPdf(
  data: BoardFloorPlanData,
  paper: FloorPlanPdfPaper,
): Promise<void> {
  const layout = floorPlanPdfLayout(data, paper);
  const host = document.createElement('div');
  host.className = 'fp-print-host';
  host.style.cssText = 'position:fixed;left:-20000px;top:0;pointer-events:none;z-index:-1;';
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    root.render(<FloorPlanPrintSheet data={data} layout={layout} dateLabel={floorPlanPdfDate()} />);
    await waitFrames(3);
    const sheet = host.querySelector('svg.fp-print-sheet') as SVGSVGElement | null;
    if (!sheet) throw new Error('Floor plan sheet did not render.');
    const png = await svgToPngDataUrl(sheet, layout.widthPx, layout.heightPx);
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({
      orientation: layout.orientation,
      unit: 'in',
      format: [layout.pageW, layout.pageH],
    });
    doc.addImage(png, 'PNG', 0, 0, layout.pageW, layout.pageH, undefined, 'FAST');
    doc.save(layout.filename);
  } finally {
    root.unmount();
    host.remove();
  }
}
